import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { PerProductEligibilityProto } from "@noom/noom-contracts/noom_contracts/billing/product_catalog/product_eligibility_service";

import { evaluateProductEligibility } from "src/utils/api/productEligibility";
import {
  ADDON_SKU,
  ADDON_SKU_TO_PRODUCT_NAME,
} from "src/utils/addonEligibility";
import { createAppAsyncThunk } from "../async-thunk";
import {
  isRequestSuccess,
  requestState,
  type RequestState,
} from "../requestState";
import getStore, { type CoreReduxState } from "../store";

export const PEPTIDE_SERMORELIN_PRODUCT_NAME =
  ADDON_SKU_TO_PRODUCT_NAME[ADDON_SKU.PEPTIDE_SERMORELIN];

export type ProductEligibilityState = Record<
  string,
  RequestState<PerProductEligibilityProto>
>;

const productEligibilitySlice = createSlice({
  name: "productEligibility",
  initialState: {} as ProductEligibilityState,
  reducers: {
    setProductEligibility(
      state,
      action: PayloadAction<{
        productName: string;
        value: RequestState<PerProductEligibilityProto>;
      }>
    ) {
      return {
        ...state,
        [action.payload.productName]: action.payload.value,
      };
    },
  },
});

export const { setProductEligibility } = productEligibilitySlice.actions;

export default productEligibilitySlice;

const inFlightByProduct: Record<string, Promise<void>> = {};

function normalizeProductNames(productNames: string | string[]) {
  const names = Array.isArray(productNames) ? productNames : [productNames];
  return [...new Set(names.filter(Boolean))];
}

export const loadProductEligibility = createAppAsyncThunk<
  string | string[],
  void
>(
  "productEligibility/loadProductEligibility",
  async (productNames, thunkAPI) => {
    const names = normalizeProductNames(productNames);
    if (names.length === 0) {
      return;
    }

    // Always fetch at the decision point so a prior success (including
    // INELIGIBLE) cannot go stale. Only dedupe in-flight requests.
    const namesNeedingFetch = names.filter((name) => !inFlightByProduct[name]);
    if (namesNeedingFetch.length > 0) {
      const batchPromise = (async () => {
        try {
          for (const productName of namesNeedingFetch) {
            thunkAPI.dispatch(
              setProductEligibility({
                productName,
                value: requestState.loading<PerProductEligibilityProto>(),
              })
            );
          }

          const response = await evaluateProductEligibility(namesNeedingFetch);
          const byName = new Map(
            response.results.map((result) => [result.productName, result])
          );

          for (const productName of namesNeedingFetch) {
            const result = byName.get(productName);
            thunkAPI.dispatch(
              setProductEligibility({
                productName,
                value: result
                  ? requestState.success(result)
                  : requestState.failure<PerProductEligibilityProto>(),
              })
            );
          }
        } catch {
          for (const productName of namesNeedingFetch) {
            thunkAPI.dispatch(
              setProductEligibility({
                productName,
                value: requestState.failure<PerProductEligibilityProto>(),
              })
            );
          }
        } finally {
          for (const productName of namesNeedingFetch) {
            delete inFlightByProduct[productName];
          }
        }
      })();

      for (const productName of namesNeedingFetch) {
        inFlightByProduct[productName] = batchPromise;
      }
    }

    await Promise.all(names.map((name) => inFlightByProduct[name]));
  }
);

export function getProductEligibility(
  productName: string,
  state: CoreReduxState = getStore().getState()
): RequestState<PerProductEligibilityProto> | undefined {
  return state.productEligibility?.[productName];
}

export function isProductEligible(
  productName: string,
  state: CoreReduxState = getStore().getState()
) {
  const entry = getProductEligibility(productName, state);
  if (!isRequestSuccess(entry)) {
    return false;
  }

  const eligibilityState = entry.data?.purchaseEligibility?.eligibilityState;
  return eligibilityState === "ELIGIBLE";
}
